import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import InfoPage from ".";

const STATE = {
  LOADING: "loading",
  SET_PASSWORD: "set_password",
  SAVING_PASSWORD: "saving_password",
  DONE: "done",
  ALREADY_SET: "already_set",
  ERROR: "error",
  NO_SESSION: "no_session",
};

const PartnerWelcome = () => {
  const location = useLocation();
  const [state, setState] = useState(STATE.LOADING);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState("");
  const didFinalize = useRef(false);

  const params = new URLSearchParams(location.search);
  const sessionId = params.get("session_id");

  useEffect(() => {
    try {
      const storedEmail = localStorage.getItem("postaway_email");
      if (window.rewardful && storedEmail) {
        window.rewardful("convert", { email: storedEmail });
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setState(STATE.NO_SESSION);
      return;
    }
    if (didFinalize.current) return;
    didFinalize.current = true;

    (async () => {
      try {
        const res = await fetch("/api/partner/finalize-checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId }),
        });
        const data = await res.json();
        if (!res.ok || !data.ok) {
          throw new Error(data?.error || "finalize_failed");
        }
        setEmail(data.email || "");
        if (data.alreadyHadPassword) {
          setState(STATE.ALREADY_SET);
        } else {
          setToken(data.token || "");
          setState(STATE.SET_PASSWORD);
        }
      } catch (err) {
        console.error("Finalize error:", err);
        setState(STATE.ERROR);
      }
    })();
  }, [sessionId]);

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setFormError("");

    if (password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setFormError("Passwords don't match.");
      return;
    }

    setState(STATE.SAVING_PASSWORD);
    try {
      const resetRes = await fetch("/api/user/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword: password }),
      });
      const resetData = await resetRes.json();
      if (!resetRes.ok || !resetData.ok) {
        throw new Error(resetData?.error || "reset_failed");
      }

      const loginRes = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const loginData = await loginRes.json();

      if (loginRes.ok && loginData.ok) {
        try {
          localStorage.setItem("postaway_email", email);
          localStorage.setItem("postaway_user_type", "partner");
          if (loginData.token) localStorage.setItem("postaway_auth_token", loginData.token);
          if (loginData.user) localStorage.setItem("postaway_user_data", JSON.stringify(loginData.user));
        } catch (_) {}
      }

      setState(STATE.DONE);
      setTimeout(() => {
        window.location.assign("/partner-settings");
      }, 1500);
    } catch (err) {
      console.error("Set password error:", err);
      setFormError(
        err.message === "invalid_or_expired_token"
          ? "This setup link has expired. Check your email for a new one, or use Forgot Password on the login page."
          : "Something went wrong setting your password. Please try again."
      );
      setState(STATE.SET_PASSWORD);
    }
  };

  const renderContent = () => {
    if (state === STATE.LOADING) {
      return (
        <div className="pw-card">
          <div className="pw-spinner" />
          <h1 className="pw-title">Setting up your account…</h1>
          <p className="pw-subtitle">This takes just a moment.</p>
        </div>
      );
    }

    if (state === STATE.NO_SESSION) {
      return (
        <div className="pw-card">
          <CheckIcon />
          <h1 className="pw-title">Welcome to Postaway!</h1>
          <p className="pw-subtitle">
            Your payment was received. Check your email for a link to set your
            password, or log in below if you've already set one up.
          </p>
          <div className="pw-actions">
            <a className="pw-btn primary" href="/partner-login">Go to Partner Login</a>
            <a className="pw-btn secondary" href="/forgot-password">Forgot Password?</a>
          </div>
        </div>
      );
    }

    if (state === STATE.ERROR) {
      return (
        <div className="pw-card">
          <h1 className="pw-title">Something went wrong</h1>
          <p className="pw-subtitle">
            We couldn't verify your checkout session. Don't worry — your payment
            is safe. Check your email for a setup link, or contact us for help.
          </p>
          <div className="pw-actions">
            <a className="pw-btn primary" href="/partner-login">Go to Partner Login</a>
            <a className="pw-btn secondary" href="/contact">Contact us</a>
          </div>
        </div>
      );
    }

    if (state === STATE.ALREADY_SET) {
      return (
        <div className="pw-card">
          <CheckIcon />
          <h1 className="pw-title">You're all set!</h1>
          <p className="pw-subtitle">
            Your account already has a password. Log in to manage your partner profile.
          </p>
          <div className="pw-actions">
            <a className="pw-btn primary" href="/partner-login">Log in</a>
          </div>
        </div>
      );
    }

    if (state === STATE.DONE) {
      return (
        <div className="pw-card">
          <CheckIcon />
          <h1 className="pw-title">Password set — you're in!</h1>
          <p className="pw-subtitle">Taking you to your partner settings…</p>
        </div>
      );
    }

    // STATE.SET_PASSWORD or STATE.SAVING_PASSWORD
    return (
      <div className="pw-card">
        <CheckIcon />
        <h1 className="pw-title">Payment received — one last step</h1>
        <p className="pw-subtitle">
          Create a password so you can log in anytime to manage your partner
          profile, discount codes, and billing.
        </p>

        <form className="pw-form" onSubmit={handleSetPassword}>
          <div className="pw-field">
            <label htmlFor="pw-email">Email</label>
            <input id="pw-email" type="email" value={email} disabled />
          </div>

          <div className="pw-field">
            <label htmlFor="pw-pass">Password</label>
            <div className="pw-pass-wrap">
              <input
                id="pw-pass"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                placeholder="At least 8 characters"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="pw-pass-toggle"
                onClick={() => setShowPassword((s) => !s)}
                aria-label="Toggle password visibility"
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div className="pw-field">
            <label htmlFor="pw-confirm">Confirm password</label>
            <input
              id="pw-confirm"
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              placeholder="Re-enter your password"
              autoComplete="new-password"
            />
          </div>

          {formError && <div className="pw-error">{formError}</div>}

          <button
            type="submit"
            className="pw-btn primary"
            disabled={state === STATE.SAVING_PASSWORD}
          >
            {state === STATE.SAVING_PASSWORD ? "Setting password…" : "Set password & continue"}
          </button>
        </form>

        <p className="pw-email-note">
          We've also emailed you a setup link in case you need to come back later.
        </p>
      </div>
    );
  };

  const below = (
    <div className="pw-page">
      {renderContent()}
      <PartnerWelcomeStyles />
    </div>
  );

  return <InfoPage title="Welcome, Partner" below={below} disableStamps />;
};

const CheckIcon = () => (
  <div className="pw-check" aria-hidden="true">
    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12.5l2.5 2.5L16 9.5" />
    </svg>
  </div>
);

const PartnerWelcomeStyles = () => (
  <style>{`
    .pw-page {
      grid-column: 1 / -1;
      width: 100%;
      max-width: 560px;
      margin: 0 auto;
      padding: 32px 24px 64px;
    }
    @media (max-width: 768px) {
      .pw-page { padding: 20px 16px 48px; }
    }
    .pw-card {
      background: #fff;
      border: 1px solid #E6E8EC;
      border-radius: 16px;
      padding: 40px 40px 32px;
      box-shadow: 0 6px 24px rgba(15, 8, 75, 0.06);
      text-align: center;
    }
    @media (max-width: 768px) {
      .pw-card { padding: 28px 20px 24px; }
    }
    .pw-check {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #dc1d08;
      margin-bottom: 12px;
    }
    .pw-title {
      font-family: 'Sailors', serif;
      font-size: 26px;
      color: #0f084b;
      margin: 0 0 8px;
    }
    @media (max-width: 768px) {
      .pw-title { font-size: 22px; }
    }
    .pw-subtitle {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 15px;
      color: #353945;
      margin: 0 0 24px;
      line-height: 1.55;
    }
    .pw-form {
      text-align: left;
      display: grid;
      gap: 14px;
      margin-bottom: 16px;
    }
    .pw-field {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }
    .pw-field label {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 600;
      color: #0f084b;
    }
    .pw-field input {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14px;
      color: #111827;
      border: 1px solid #d1d5db;
      border-radius: 10px;
      padding: 10px 12px;
      background: #fff;
      outline: none;
      transition: border-color 0.15s, box-shadow 0.15s;
      width: 100%;
      box-sizing: border-box;
    }
    .pw-field input:disabled {
      background: #f3f4f6;
      color: #6b7280;
    }
    .pw-field input:focus {
      border-color: #0f084b;
      box-shadow: 0 0 0 3px rgba(15, 8, 75, 0.12);
    }
    .pw-pass-wrap {
      position: relative;
      display: flex;
    }
    .pw-pass-wrap input { flex: 1; padding-right: 60px; }
    .pw-pass-toggle {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: #6b7280;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      padding: 4px;
    }
    .pw-error {
      background: #fff1f0;
      border: 1px solid #fecaca;
      color: #991b1b;
      border-radius: 10px;
      padding: 10px 12px;
      font-size: 14px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
    }
    .pw-actions {
      display: flex;
      gap: 12px;
      justify-content: center;
      flex-wrap: wrap;
      margin-bottom: 12px;
    }
    .pw-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 12px 24px;
      border-radius: 100px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 15px;
      font-weight: 600;
      text-decoration: none;
      transition: all 0.2s;
      min-width: 200px;
      border: 2px solid transparent;
      cursor: pointer;
      width: 100%;
    }
    .pw-btn.primary {
      background: #dc1d08;
      color: #fff;
      border-color: #dc1d08;
    }
    .pw-btn.primary:hover:not(:disabled) {
      background: #b51706;
      border-color: #b51706;
    }
    .pw-btn.primary:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
    .pw-btn.secondary {
      background: transparent;
      color: #0f084b;
      border-color: #0f084b;
    }
    .pw-btn.secondary:hover {
      background: #0f084b;
      color: #fff;
    }
    .pw-email-note {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      color: #777E90;
      text-align: center;
      margin: 0;
    }
    .pw-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid #e5e7eb;
      border-top-color: #dc1d08;
      border-radius: 50%;
      margin: 0 auto 16px;
      animation: pw-spin 0.8s linear infinite;
    }
    @keyframes pw-spin {
      to { transform: rotate(360deg); }
    }
  `}</style>
);

export default PartnerWelcome;
